import { useEffect, useState } from 'react';
import axios from 'axios';
import Reveal from '../components/site/Reveal';
import { useT } from '../i18n/LanguageContext';
import { Play, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Media() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/media`).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div data-testid="page-media">
      <section className="pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">{t('media.eyebrow')}</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim max-w-4xl">
              {t('media.title')}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          {loading ? (
            <p className="text-sec font-mono text-sm">{t('common.loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-sec">{t('media.empty')}</p>
          ) : (
            <div className="space-y-6">
              {items.map((v, i) => (
                <Reveal key={v.id} delay={(i % 3) * 80}>
                  <div data-testid={`media-card-${v.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-6 border border-subtle surface-2 rounded-md card-glow overflow-hidden">
                    <button
                      onClick={() => setActive(v)}
                      data-testid={`media-play-${v.id}`}
                      className="md:col-span-7 relative aspect-video w-full overflow-hidden group"
                      aria-label={`Play ${v.title}`}
                    >
                      <img
                        src={v.thumbnail_base64 || v.thumbnail_url || `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                        alt={v.title}
                        loading="lazy"
                        onError={(e) => {
                          // Fallback chain: custom → YouTube hqdefault → mqdefault → maroon placeholder
                          const target = e.currentTarget;
                          if (!target.dataset.fallback) {
                            target.dataset.fallback = '1';
                            target.src = `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`;
                          } else if (target.dataset.fallback === '1') {
                            target.dataset.fallback = '2';
                            target.src = `https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`;
                          } else {
                            target.dataset.fallback = '3';
                            target.style.background = 'linear-gradient(135deg,#7D243B 0%,#3B98AA 100%)';
                            target.src = 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 16 9%22><rect width=%2216%22 height=%229%22 fill=%22%23020617%22/></svg>';
                          }
                        }}
                        className="photo-fit transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="play-overlay">
                        <div className="play-icon"><Play size={26} fill="currentColor" /></div>
                      </div>
                    </button>
                    <div className="md:col-span-5 p-6 md:p-8 flex flex-col justify-center">
                      <p className="eyebrow mb-3">— {t('common.watch')}</p>
                      <h3 className="font-display text-2xl md:text-3xl text-prim tracking-tight leading-tight">{v.title}</h3>
                      {v.description && <p className="mt-4 text-sec leading-relaxed">{v.description}</p>}
                      <button onClick={() => setActive(v)} className="btn-secondary mt-6 self-start text-sm">
                        <Play size={14} fill="currentColor" /> {t('common.watch')}
                      </button>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {active && (
        <div
          data-testid="media-lightbox"
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            onClick={() => setActive(null)}
            className="absolute top-6 right-6 text-white p-2 rounded-full hover:bg-white/10"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              data-testid="media-iframe"
              src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1&rel=0`}
              title={active.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full rounded-lg border border-white/10"
            />
          </div>
        </div>
      )}
    </div>
  );
}
